import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MapPin, Users, Globe, Wheat, DollarSign, Filter, ChevronRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icons based on credit score
const createCustomIcon = (creditScore) => {
  let color = "#ff3b30"; // red for poor
  if (creditScore >= 750) color = "#00ff41"; // green for excellent
  else if (creditScore >= 650) color = "#0ea5e9"; // blue for good
  else if (creditScore >= 550) color = "#eab308"; // yellow for fair
  
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background: ${color};
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid #000;
      box-shadow: 0 0 8px ${color}80;
    "></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -8],
  });
};

// Map bounds fitter component
function FitBounds({ farmers }) {
  const map = useMap();
  
  useEffect(() => {
    if (farmers.length > 0) {
      const bounds = L.latLngBounds(farmers.map(f => [f.lat, f.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [farmers, map]);
  
  return null;
}

export default function GeospatialPage() {
  const [farmers, setFarmers] = useState([]);
  const [countryStats, setCountryStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [hoveredFarmer, setHoveredFarmer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGeospatialData();
  }, []);

  const fetchGeospatialData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/geospatial/farmers");
      setFarmers(data.farmers || []);
      setCountryStats(data.country_stats || {});
    } catch (err) {
      console.error("Failed to load geospatial data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredFarmers = useMemo(() => {
    return farmers.filter(f => {
      if (selectedCountry !== "all" && f.country !== selectedCountry) return false;
      if (selectedStatus !== "all" && f.status !== selectedStatus) return false;
      return true;
    });
  }, [farmers, selectedCountry, selectedStatus]);

  const countries = useMemo(() => [...new Set(farmers.map(f => f.country))].sort(), [farmers]);

  const scoreColor = (s) => s >= 750 ? "#00ff41" : s >= 650 ? "#0ea5e9" : s >= 550 ? "#eab308" : "#ff3b30";

  return (
    <div data-testid="geospatial-page" className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Geospatial View
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            {filteredFarmers.length} farmers mapped across {countries.length} countries
          </p>
        </div>
        
        {/* Filters */}
        <div className="flex gap-2">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger data-testid="country-filter" className="w-[140px] h-8 rounded-none border-zinc-800 bg-black text-xs font-mono">
              <Globe className="w-3 h-3 mr-2" />
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
              <SelectItem value="all" className="text-xs font-mono">All Countries</SelectItem>
              {countries.map(c => (
                <SelectItem key={c} value={c} className="text-xs font-mono">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger data-testid="status-filter" className="w-[120px] h-8 rounded-none border-zinc-800 bg-black text-xs font-mono">
              <Filter className="w-3 h-3 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
              <SelectItem value="all" className="text-xs font-mono">All Status</SelectItem>
              <SelectItem value="active" className="text-xs font-mono">Active</SelectItem>
              <SelectItem value="inactive" className="text-xs font-mono">Inactive</SelectItem>
              <SelectItem value="pending" className="text-xs font-mono">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Map */}
        <Card className="lg:col-span-3 bg-zinc-950/50 border-zinc-800 rounded-none overflow-hidden">
          <CardContent className="p-0 h-[600px]">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-600" />
              </div>
            ) : (
              <MapContainer
                center={[-5, 32]}
                zoom={4}
                style={{ height: "100%", width: "100%", background: "#0a0a0c" }}
                className="map-dark"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                <FitBounds farmers={filteredFarmers} />
                {filteredFarmers.map((farmer) => (
                  <Marker
                    key={farmer.id}
                    position={[farmer.lat, farmer.lng]}
                    icon={createCustomIcon(farmer.credit_score)}
                    eventHandlers={{
                      mouseover: () => setHoveredFarmer(farmer),
                      mouseout: () => setHoveredFarmer(null),
                      click: () => navigate(`/farmers/${farmer.id}`),
                    }}
                  >
                    <Popup className="custom-popup">
                      <div className="bg-zinc-900 p-3 min-w-[200px] border border-zinc-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono font-bold text-zinc-100 text-sm">
                            {farmer.first_name} {farmer.last_name}
                          </span>
                          <Badge 
                            className={`rounded-none text-[8px] ${
                              farmer.status === "active" 
                                ? "bg-[#00ff41]/10 text-[#00ff41]" 
                                : "bg-zinc-800 text-zinc-500"
                            }`}
                          >
                            {farmer.status}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-[10px] font-mono text-zinc-400">
                          <div className="flex justify-between">
                            <span>Location:</span>
                            <span className="text-zinc-300">{farmer.region}, {farmer.country}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Credit Score:</span>
                            <span style={{ color: scoreColor(farmer.credit_score) }} className="font-bold">
                              {farmer.credit_score}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Farm Size:</span>
                            <span className="text-zinc-300">{farmer.farm_size_ha} ha</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Revenue:</span>
                            <span className="text-[#00ff41]">${farmer.total_revenue_usd?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Crops:</span>
                            <span className="text-zinc-300">{(farmer.crops || []).join(", ")}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="w-full mt-3 h-6 rounded-none bg-[#00ff41]/10 text-[#00ff41] text-[10px] font-mono hover:bg-[#00ff41]/20"
                          onClick={() => navigate(`/farmers/${farmer.id}`)}
                        >
                          View Profile <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </CardContent>
        </Card>

        {/* Stats Sidebar */}
        <div className="space-y-4">
          {/* Legend */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
                Credit Score Legend
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {[
                { label: "Excellent (750+)", color: "#00ff41" },
                { label: "Good (650-749)", color: "#0ea5e9" },
                { label: "Fair (550-649)", color: "#eab308" },
                { label: "Poor (<550)", color: "#ff3b30" },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full border-2 border-black"
                    style={{ backgroundColor: item.color, boxShadow: `0 0 6px ${item.color}80` }}
                  />
                  <span className="text-[10px] font-mono text-zinc-400">{item.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Country Stats */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
                Country Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[340px]">
                <div className="px-4 pb-4 space-y-2">
                  {Object.entries(countryStats)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([country, stats]) => (
                      <button
                        key={country}
                        data-testid={`country-stat-${country}`}
                        className={`w-full p-3 text-left bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors ${
                          selectedCountry === country ? "border-[#00ff41]" : ""
                        }`}
                        onClick={() => setSelectedCountry(selectedCountry === country ? "all" : country)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-zinc-200">{country}</span>
                          <Badge variant="outline" className="rounded-none text-[9px] border-zinc-700 font-mono">
                            {stats.count}
                          </Badge>
                        </div>
                        <div className="flex gap-4 text-[10px] font-mono">
                          <span className="text-zinc-500">
                            Avg Score: <span style={{ color: scoreColor(stats.avg_credit) }}>{stats.avg_credit}</span>
                          </span>
                          <span className="text-zinc-500">
                            Revenue: <span className="text-[#00ff41]">${Math.round(stats.total_revenue / 1000)}k</span>
                          </span>
                        </div>
                      </button>
                    ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Hovered Farmer Info Bar */}
      {hoveredFarmer && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-zinc-900 border border-zinc-700 px-6 py-3 z-[1000] shadow-lg">
          <div className="flex items-center gap-6 text-xs font-mono">
            <span className="text-zinc-200">
              <MapPin className="w-3 h-3 inline mr-1 text-[#00ff41]" />
              {hoveredFarmer.first_name} {hoveredFarmer.last_name}
            </span>
            <span className="text-zinc-400">{hoveredFarmer.region}, {hoveredFarmer.country}</span>
            <span style={{ color: scoreColor(hoveredFarmer.credit_score) }}>
              Score: {hoveredFarmer.credit_score}
            </span>
            <span className="text-[#00ff41]">${hoveredFarmer.total_revenue_usd?.toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Custom styles for dark map popups */}
      <style>{`
        .leaflet-popup-content-wrapper {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
        }
        .leaflet-popup-tip-container {
          display: none;
        }
        .leaflet-container {
          background: #0a0a0c !important;
        }
      `}</style>
    </div>
  );
}
